import { CloseButton, Modal } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";

import classes from "./DetailModal.module.css";

export interface DetailCarousel {
  onPrevious: () => void;
  onNext: () => void;
}

export interface DetailCardProps {
  /** The media pane, on the left. */
  media: ReactNode;
  /** The details pane, on the right. */
  children: ReactNode;
  /** Above the card, on the right: the actions on what the media pane shows. */
  actions?: ReactNode;
  /** Several items: round controls at the pane's edges, and the arrow keys, move between them. */
  carousel?: DetailCarousel | undefined;
}

/**
 * The detail card of design-system.md 3.4: the media in a gray pane on the left, the details on
 * the right, side by side up to the detail width and stacked below `md`. The modal and a page
 * render the same card.
 */
export function DetailCard({ media, children, actions, carousel }: DetailCardProps) {
  useHotkeys(
    carousel === undefined
      ? []
      : [
          ["ArrowLeft", carousel.onPrevious],
          ["ArrowRight", carousel.onNext],
        ],
  );
  return (
    <div className={classes.frame}>
      {actions === undefined ? null : <div className={classes.actions}>{actions}</div>}
      <div className={classes.card}>
        <div className={classes.pane}>
          {media}
          {carousel === undefined ? null : (
            <>
              <button
                type="button"
                className={classes.control}
                data-side="previous"
                aria-label="Previous output"
                onClick={carousel.onPrevious}
              >
                <ChevronLeft size={20} aria-hidden />
              </button>
              <button
                type="button"
                className={classes.control}
                data-side="next"
                aria-label="Next output"
                onClick={carousel.onNext}
              >
                <ChevronRight size={20} aria-hidden />
              </button>
            </>
          )}
        </div>
        <div className={classes.details}>{children}</div>
      </div>
    </div>
  );
}

export interface DetailModalProps {
  opened: boolean;
  onClose: () => void;
  /** Names the dialog for a screen reader. */
  label: string;
  /** Top left, over the veil: who or what, and when. */
  header: ReactNode;
  /** The `DetailCard`. */
  children: ReactNode;
}

/**
 * The full-screen detail view (design-system.md 3.4): the page shows through a blurred veil, the
 * header and the close button sit on it in white, and the card is centered. Escape, the close
 * button and a click outside the card close it.
 */
export function DetailModal({ opened, onClose, label, header, children }: DetailModalProps) {
  // The content covers the screen, so "outside the card" is a click on the content itself.
  const onBackdrop = (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) onClose();
  };
  return (
    <Modal.Root opened={opened} onClose={onClose} fullScreen>
      <Modal.Overlay className={classes.overlay} />
      <Modal.Content className={classes.content} aria-label={label}>
        <div className={classes.header}>
          <div className={classes.who}>{header}</div>
          <CloseButton
            size="lg"
            aria-label="Close"
            className={classes.close}
            onClick={onClose}
          />
        </div>
        <Modal.Body className={classes.body} onClick={onBackdrop}>
          {children}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
